"use server";

import { v4 as uuidv4 } from "uuid";

import { auth } from "@/auth";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import { requireRole } from "@/lib/permissions";
import {
  addDocumentSignataireSchema,
  deleteDocumentSignataireSchema,
  editDocumentSignataireSchema,
} from "@/lib/schemas/document-signataires";
import { prisma } from "@/prisma";
import type { ActionResult } from "@/types/server-action";

export async function addDocumentSignataire(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = addDocumentSignataireSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const { documentCode, signerIdentifier, firstName, lastName } = parsed.data;

  const existing = await prisma.documentSignataire.findFirst({
    where: {
      deletedAt: null,
      documentCode,
      signerIdentifier,
    },
  });

  if (existing) {
    return {
      success: false,
      message:
        "Conflit : un signataire actif existe déjà pour ce document et cet identifiant.",
    };
  }

  await prisma.documentSignataire.create({
    data: {
      documentCode,
      signerIdentifier,
      firstName,
      lastName,
      isActive: true,
      version: 1,
      versionGroupId: uuidv4(),
      versionNote: null,
      deletedAt: null,
    },
  });

  return { success: true };
}

export async function editDocumentSignataire(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = editDocumentSignataireSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const {
    id,
    documentCode,
    signerIdentifier,
    firstName,
    lastName,
    isActive,
    versionNote,
  } = parsed.data;

  const existing = await prisma.documentSignataire.findFirst({
    where: { id, deletedAt: null },
  });

  if (!existing) {
    return { success: false, message: "Signataire introuvable" };
  }

  const mappingChanged =
    existing.documentCode !== documentCode ||
    existing.signerIdentifier !== signerIdentifier;

  if (mappingChanged) {
    const conflict = await prisma.documentSignataire.findFirst({
      where: {
        deletedAt: null,
        documentCode,
        signerIdentifier,
      },
    });

    if (conflict) {
      return {
        success: false,
        message:
          "Conflit : un signataire actif existe déjà pour la clé cible (document, identifiant).",
      };
    }
  }

  await prisma.$transaction([
    prisma.documentSignataire.update({
      where: { id },
      data: { deletedAt: new Date(), isActive: false },
    }),
    prisma.documentSignataire.create({
      data: {
        documentCode,
        signerIdentifier,
        firstName,
        lastName,
        isActive,
        version: existing.version + 1,
        versionGroupId: existing.versionGroupId,
        versionNote: versionNote ?? null,
        deletedAt: null,
      },
    }),
  ]);

  return { success: true };
}

export async function deleteDocumentSignataire(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = deleteDocumentSignataireSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const { id } = parsed.data;

  await prisma.documentSignataire.update({
    where: { id },
    data: { deletedAt: new Date(), isActive: false },
  });

  return { success: true };
}
